import { observable, action, makeObservable, computed } from 'mobx';
import { formatMessage } from 'umi';
import { init } from '@/utils/NERTC';
import { meeting, user, rtc, members } from './index';
import { getInterpretChannels, subscribe } from '@/api';
import { getFormatMessage } from '@/utils/locale';
import { Modal } from '@/components';

export const MainChannel = 'no-channel';

export enum InterpretationStatus {
  starting = 2,
  stoped = 1,
}

class SimultaneousInterpretation {
  constructor() {
    makeObservable(this);
    this.client = null;
  }

  client: any; // 暂时不需要被observable

  /**
   * no-channel字符表示的是主频道 其他的取接口的languageTag
   */
  @observable curChannel = MainChannel;

  /**
   * 服务端给的频道接口列表。实际渲染需要加上写死的主频道，主频道独立，和子频道不相关
   */
  @observable channels: Array<defs.api.LanguageChannels> = [];

  /**
   * 用户和流的映射 一个用户对应一个流 本地需要把远程用户的流订阅才能播放
   */
  @observable streamMap = new Map();

  /**
   * 同声传译开启状态 1是关闭 2是开启
   */
  @observable interpretationStatus: InterpretationStatus = InterpretationStatus.stoped;

  /**
   * 主频道是否静音
   * 主频道静音一定默认是false 否则在无同声传译中 在rtc中实例 stream-added 回调的时候会阻碍自动播放
   */
  @observable mainChannelMute = false;

  // 开启同传时，默认接收主频道，切换到子频道后，主频道会静音，此字段表示是否主动开启接收主频道音频
  @observable isReceiveMainChannel = false;
  // 主频道声音大小(UI 层面上的，并不是实际音量) 默认 70
  @observable mainChannelVolume = 70;

  isFirstToggleChannel = true;

  @computed
  get allChannels() {
    this.channels.unshift({
      languageName: getFormatMessage('MAIN_CHANNEL'),
      languageTag: '-1',
      yxSubchannelName: MainChannel, // 子频道的房间都是以房间名统一 名称一样大家就在一起
    });
    return this.channels;
  }
  // 当前 channel 数据
  @computed
  get currentChannelItem() {
    return this.allChannels?.find((item) => item.yxSubchannelName === this.curChannel);
  }

  // 是否选择了主频道
  @computed
  get isMainChannel() {
    return this.currentChannelItem?.yxSubchannelName === MainChannel;
  }

  // 当前收听频道的译员
  @computed
  get currentChannelInterpreters() {
    return members?.interpreters.filter(
      (item) => item.outputChannelLanguage === this.currentChannelItem?.languageTag
    );
  }

  /**
   * 新增流
   * @param sterm
   */
  // @action
  // async addStream(sterm: Stream) {
  //   const yxuid = sterm.getId();
  //   if (!this.streamMap.get(yxuid)) {
  //     this.streamMap.set(yxuid, sterm);
  //   }
  // }

  /**
   * 移除流
   * @param sterm
   */
  // @action
  // async removeStream(sterm: Stream) {
  //   const yxuid = sterm.getId();
  //   if (this.streamMap.get(yxuid)) {
  //     this.streamMap.delete(yxuid);
  //   }
  // }

  /**
   * 获取当前的频道列表
   * @param channelId
   * @returns
   */
  @action
  async getChannels(params: { channelId?: string; meetingId: string }) {
    const ret = await getInterpretChannels(params);
    console.log(meeting);
    if (ret.status === 1) {
      const channels = ret.data?.channels || [];
      this.channels = channels;
      return channels;
    }
    return [];
  }

  /**
   * 切换房间, 切换的时候要销毁实例, web端没有一个实例加入多房间的概念,每加入一个房间都是重新生成 当前只操作子房间
   * @param channelId
   * @returns
   */
  @action
  async setCurChannel(curChannelName: string, languageTag: string) {
    if (this.curChannel === curChannelName) {
      return;
    }
    if (meeting.appKey) {
      const preChannel = this.curChannel;
      this.curChannel = curChannelName;
      if (this.client) {
        try {
          this.client.leave();
          this.client.destroy();
          this.client = null;
        } catch (error) {
          console.error('销毁实例失败', error);
        }
      }

      if (curChannelName === MainChannel) {
        // 取消订阅所有子频道(subscriptions 为空表示不订阅)
        subscribe({
          channelId: meeting.info?.channelId,
          subscriptions: [],
        });
        // 设置主频道音量为 100 。
        // this.isPlayMainChannel(true);
        this.setMainChannelVolume(100, false);
      } else {
        // 创建新的实例和频道
        const client = await init({
          appKey: meeting.appKey,
          activeSpeaker: (data) => {
            meeting.setSpeakerYxUid(data.uid);
          },
          addStream: (stream) => {
            this.streamMap.set(stream.getId(), stream);
          },
          removeStream: (stream) => {
            this.streamMap.delete(stream.getId());
          },
          notAllowedError: () => {
            const { streamMap } = this;
            Modal.warning({
              title: formatMessage({ id: 'MIC_INCOMPATIBLE' }),
              content: formatMessage({ id: 'MIC_CLICK_ABLE' }),
              okText: formatMessage({ id: 'CONFIRM' }),
              okButtonProps: {
                className: `popo-meeting-button`,
              },
              onOk() {
                [...streamMap.values()].forEach((stream: any) => {
                  console.log('setCurChannel-主频道notAllowedError事件触发');
                  stream.play();
                });
              },
            });
          },
        });
        this.client = client;
        // 调节语言频道的音量为 100 且不能修改
        this.client.setPlaybackVolume(100);

        // 更新订阅频道信息 先调用服务端接口-服务端有自己的逻辑要处理，但是和云信sdk无关
        const ret = await subscribe({
          channelId: meeting.info?.channelId,
          subscriptions: [{ languageTag }],
        });
        if (ret.status === 1) {
          client.join({
            channelName: curChannelName,
            uid: user.yxuid || '',
            token: meeting.info?.token || '',
          });
        }
        // 如果用户没有主动选择【同时收听主频道】, 切换子频道时， 主频道音量设为 0, 否则按记录值设置主频道音量
        if (!this.isReceiveMainChannel) {
          this.setMainChannelVolume(0, false);
        } else {
          this.setMainChannelVolume(this.mainChannelVolume);
        }
        // // 首次切换主频道时， 主频道需要静音，后续切子频道并且选择接了收主频道,需要记录选择接收主频道的状态，用于再次切换子频道时，是否接收主频道状态的回显
        // if (this.isFirstToggleChannel) {
        //   this.isFirstToggleChannel = false;
        //   this.setMainChannelVolume(0, false);
        //   // this.isPlayMainChannel(false);
        //   // this.setMainChannelMute(true);
        //   return;
        // }
        // if (preChannel === MainChannel && this.isReceiveMainChannel) {
        //   // this.setMainChannelMute(this.mainChannelMute);
        //   // this.isPlayMainChannel(this.isReceiveMainChannel);
        //   // 如果是主频道切到子频道 是否静音主频道和上次保持一致
        //   // 同时收听主频道恢复到上一次的选择，如果没有默认音量为 70
        //   this.setMainChannelVolume(this.mainChannelVolume);
        // }
      }
      // 切换频道的时候也需要判断是否需要音量补齐，处理译员未变更状态的时候的情况
      this.setMainChannelVolumeComplement();
    }
  }

  @action
  async setInterpretationStatus(status: number) {
    this.interpretationStatus = status;
    // 如果关闭了同传，需要恢复主频道音量为 100
    if (status === InterpretationStatus.stoped) {
      this.setMainChannelVolume(100, false);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  isPlayMainChannel(isPlaying: boolean) {
    console.log(rtc.remoteStreamsMap.values(), 'values');
    [...rtc.remoteStreamsMap.values()].forEach((stream) => {
      if (isPlaying) {
        stream.play();
      } else {
        stream.stop();
      }
    });
  }
  /**
   * 设置主频道音量
   * @param volume 音量
   * @param update 音量的值是否需要被记录，默认为 true（在音量补足逻辑中，音量不需要被记录）
   */
  @action
  setMainChannelVolume(volume: number, update: boolean = true) {
    if (rtc.client) {
      rtc.client.setPlaybackVolume(volume);
      // 兼容 iOS 不支持 setPlaybackVolume 设置音量的问题（安卓偶现）,因此做个额外处理，volume 为 0 时，停止播放所有远端流音频，不为 0 时，播放所有远端流
      if (isMobile) {
        this.isPlayMainChannel(volume > 0);
      }

      if (update) {
        this.mainChannelVolume = volume;
      }
    }
  }

  // 当前只收听了一个语言频道,并且没选择 [同时接收主频道] 开启音量补足
  @action
  async setMainChannelVolumeComplement() {
    // 当前频道无译员开麦, 主频道 恢复 100% 音量，但是数值还显示 70
    // 当前频道无译员, 主频道 恢复 100% 音量，但是数值还显示 70
    if (!this.isMainChannel && !this.isReceiveMainChannel) {
      if (
        this.currentChannelInterpreters?.every((item) => item.mikeStatus === 2) ||
        this.currentChannelInterpreters?.length === 0
      ) {
        this.setMainChannelVolume(100, false);
      } else {
        this.setMainChannelVolume(0, false);
      }
    }
  }

  /**
   * 设置主频道是否静音
   * @param status
   */
  // @action
  // async setMainChannelMute(status: boolean) {
  //   this.mainChannelMute = status;
  //   this.isPlayMainChannel(!status);
  // }

  /**
   * receiveMainChannel 只在用户主动选择 [同时接收主频道] 才调用
   * 设置接收主频道,并播放
   * @param status
   */
  @action
  async receiveMainChannel(status: boolean) {
    this.isReceiveMainChannel = status;
    // this.isPlayMainChannel(status);
    this.setMainChannelVolume(status ? this.mainChannelVolume : 0, false);
    // 关闭主频道，再次检测是否需要音量补全
    if (!status) {
      this.setMainChannelVolumeComplement();
    }
  }

  @action
  async destroy() {
    if (this.client) {
      await this.client.leave();
      this.client.destroy();
    }
  }
}

export default SimultaneousInterpretation;
